import * as Moment from 'moment';
import * as XLSX from 'xlsx';

export class Import {

    protected columnOffset: number;
    protected readonly dataLoaded: any;
    protected isCsv: boolean;
    protected isParsing: boolean;
    protected readonly parsingOptions: XLSX.ParsingOptions;
    protected rowOffset: number;
    protected sheet: string;
    protected readonly sheets: string[] = [];
    protected readonly workbook: XLSX.WorkSheet;

    constructor(data: any, opts?: XLSX.ParsingOptions) {
        this.columnOffset = 0;
        this.dataLoaded = data;
        this.isCsv = false;
        this.isParsing = false;
        this.parsingOptions = opts || {};
        this.rowOffset = 0;
        this.sheet = null;
        this.workbook = data ? this.getWorkbook(data, opts) || {} : {};
        this.sheets = this.workbook.SheetNames || [];
    }

    protected formatDecimalSeparator(data: any, cell: number[]): any {
        const text: string = this.getFormattedText(cell);

        return this.getType(cell) === 'n' && text !== null
            ? +text.replace(',', '.')
            : data;
    }

    protected formatNumber(data: any): any {
        if (data === (+data).toString()) {
            return +data;
        } else if (data.indexOf(',') !== -1) {
            const value: string = data.replace(',', '.');
            return value === (+value).toString() ? +value : data;
        }

        return data;
    }

    protected formatValue(data: any, cell: number[]): any {
        const moment = (format?: string): any => {
            const value: any = this.getValue(cell);
            const datetime: number = this.getDatetime(value);
            return datetime !== null
                ? (format ? (Moment.unix(+datetime / 1000)).format(format) : Moment.unix(+datetime / 1000))
                : null;
        };

        switch (this.getNumberFormat(cell)) {
            case 'dd/mm/yyyy\\ hh:mm:ss':
                return moment('DD.MM.YYYY HH:mm:ss') || data;
            case 'h:mm':
                return moment('HH:mm') || data;
            case 'h:mm AM/PM':
                return moment('hh:mm') || data;
            case 'h:mm:ss':
                return moment('HH:mm:ss') || data;
            case 'h:mm:ss AM/PM':
                return moment('hh:mm:ss') || data;
            case 'm/d/yy':
                return moment('DD.MM.YYYY') || data;
            case 'm/d/yy h:mm':
                return moment('DD.MM.YYYY HH:mm') || data;
            default:
                return data;
        }
    }

    protected getDatetime(value: number): number {
        if (typeof value !== 'number') {
            return null;
        }
        const moment = Moment.unix(Math.ceil(((value - (25567 + 2)) * 86400)));
        // const moment = Moment.unix(((value - (25567 + 2)) * 86400));

        return +moment - (+moment.utcOffset() * 60 * 1000);
    }

    protected getFormattedText(cell: number[]): string {
        const ref: string = this.getRef(cell) || null;
        const text: string = ((this.workbook.Sheets[this.sheet] || {})[ref] || {}).w;

        return typeof text === 'string' ? text : null;
    }

    protected getJson(name: string, opts?: XLSX.Sheet2JSONOpts): any[] {
        this.isParsing = !!(
            this.parsingOptions.cellNF && !this.parsingOptions.cellDates && !this.parsingOptions.dateNF && (opts || {}).raw !== true
        );
        this.sheet = this.workbook.Sheets[name] ? name : null;
        this.setSheetOffsets();

        return this.sheet
            ? this.parseData(XLSX.utils.sheet_to_json(
                this.workbook.Sheets[name], { header: 1, ...(this.isParsing ? { raw: false } : {}), ...(opts || {}) }
            ))
            : null;
    }

    protected getNumberFormat(cell: number[]): string {
        const ref: string = this.getRef(cell) || null;

        return ((this.workbook.Sheets[this.sheet] || {})[ref] || {}).z || null;
    }

    protected getRef(cell: number[]): string {
        if (!Array.isArray(cell) || cell.length !== 2 || typeof cell[0] !== 'number' || typeof cell[1] !== 'number') {
            return null;
        }

        return XLSX.utils.encode_cell({ c: cell[0] + +this.columnOffset, r: cell[1] + +this.rowOffset });
    }

    protected getType(cell: number[]): string {
        const ref: string = this.getRef(cell) || null;

        return ((this.workbook.Sheets[this.sheet] || {})[ref] || {}).t || null;
    }

    protected getValue(cell: number[]): any {
        const ref: string = this.getRef(cell) || null;
        const value: any = ((this.workbook.Sheets[this.sheet] || {})[ref] || {}).v;

        return typeof value !== 'undefined' ? value : null;
    }

    protected parseData(data: any[]): any[] {
        if (!this.isParsing && !this.isCsv) {
            return data;
        }

        data.forEach((row: any[], r: number) =>
            row.forEach((value: any, c: number) => {
                const cell: number[] = [c, r];

                if (this.isCsv) {
                    data[r][c] = this.formatNumber(value);
                } else if (this.isParsing) {
                    data[r][c] = this.formatValue(value, cell);
                }
            })
        );

        return data;
    }

    protected setSheetOffsets(): void {
        const data: any = this.workbook.Sheets[this.sheet] || {};
        const range: any = data['!ref'] ? XLSX.utils.decode_range(data['!ref']) : {};
        this.columnOffset = (range.s || {}).c || 0;
        this.rowOffset = (range.s || {}).r || 0;
    }
    
    public getSheetData(name: string, opts?: any): any[] {
        return this.getJson(name, opts) || [];
    }

    public getSheetDataByName(name: string, opts?: any): any[] {
        return this.sheets.indexOf(name) !== -1 ? this.getSheetData(name, opts) : [];
    }

    public getSheetRowByName(name: string, offset: number = 0, opts?: any): string[] {
        return this.sheets.indexOf(name) !== -1
            ? (this.getSheetData(name, opts)[offset] || []).map((column: string) => column)
            : [];
    }

    public getSheets(hasData: boolean = false): string[] {
        return hasData
            ? this.sheets.filter((name: string) => (this.getSheetDataByName(name) || []).length)
            : this.sheets;
    }

    protected getWorkbook(data: string, opts?: XLSX.ParsingOptions): XLSX.WorkBook {
        try {
            const workbook: any = XLSX.read(data, { type: 'binary', ...(opts ? opts : {}) });
            this.isCsv = !workbook.Workbook;
            return this.isCsv
                ? XLSX.read(data, { type: 'binary', ...(opts ? opts : {}), raw: true })
                : workbook;
        } catch (error) {
            console.log(error);
            return null;
        }
    }
}
